import { formatCurrency, formatDate } from '@if/core/utils'
import { AreaButton, ControlledChipsField, ControlledPercentField, Help, StatCard } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { PromoPlanDto, useGetCurrentPromoPlans } from '@src/data/api/merchants-api/merchants-api'
import { applyRebateToTermRates } from '@src/data/customBuydownRates'
import { CUSTOM_BUYDOWN_FAMILY } from '@src/data/types/Constants'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCustomBuydownDialog } from './CustomBuydownDialog'

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

// Minimal form shape this panel needs, shared by the merchant and banner promotion forms.
interface PromoNotificationFormValues {
  promoNotificationEmailList: string[]
  flashPlanRateRebate: number
}

interface Props {
  id: string
  /** Display-only mode, e.g. a child merchant whose parent banner owns the buydown. */
  readOnly?: boolean
  /** Locks only the rate rebate field, e.g. a user without a Buy Down rebate write level. */
  rebateReadOnly?: boolean
}

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography>{value}</Typography>
  </Grid>
)

// `rebate` is the live merchant-level rate rebate. It is subtracted from the plan's frozen base
// rates client-side, so editing the rebate updates the displayed customer rates without a refetch.
const BuydownCard = ({ plan, rebate, onEdit }: { plan: PromoPlanDto; rebate: number; onEdit?: () => void }) => {
  const { t } = useTranslation()
  const customerRates = applyRebateToTermRates(plan.promoTermRates, rebate)
  return (
    <Card variant="outlined">
      <CardHeader
        title={t('merchant.customBuydown.title')}
        slotProps={{ title: { variant: 'subtitle1', color: 'primary' } }}
        action={
          onEdit && (
            <IconButton aria-label={t('merchant.customBuydown.editTitle')} onClick={onEdit} size="small">
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          )
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack gap={2.5}>
          <Grid container columns={12} spacing={2}>
            <Fact label={t('merchant.customBuydown.planCode')} value={plan.promoCodePrefix || plan.label} />
            <Fact label={t('merchant.customBuydown.contribution')} value={formatPercent(plan.merchantFeeRate)} />
            <Fact label={t('merchant.customBuydown.adminFee')} value={formatPercent(plan.borrowerFeeRate)} />
            <Fact
              label={t('merchant.customBuydown.maxFinancingAmount')}
              value={
                plan.promoTermRates[0]?.maxFinancingAmount
                  ? formatCurrency(plan.promoTermRates[0].maxFinancingAmount)
                  : t('merchant.customBuydown.noMaximum')
              }
            />
            <Fact label={t('merchant.promo.activeFrom')} value={formatDate(plan.activeFrom)} />
            <Fact
              label={t('merchant.promo.expiresOn')}
              value={plan.expiresOn ? formatDate(plan.expiresOn) : t('merchant.promo.noExpiresOn')}
            />
          </Grid>
          <Box>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('merchant.customBuydown.customerRates')}
              </Typography>
              <Help title={t('merchant.customBuydown.customerRatesTooltip')} />
            </Stack>
            <Box
              sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5 }}
            >
              {customerRates.map((rate) => (
                <StatCard
                  key={rate.termMonths}
                  variant="outlined"
                  label={`${rate.termMonths} ${t('common.months')}`}
                  value={formatPercent(rate.promoRate)}
                />
              ))}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

/**
 * The "Custom promotion" body, reused by the merchant and banner promotion tabs. The owning
 * entity has at most one Custom Buydown. Deletion is intentionally not offered. In `readOnly`
 * mode editing and the empty-state are suppressed.
 */
const CustomPromotionPanel = ({ id, readOnly = false, rebateReadOnly = false }: Props) => {
  const { t } = useTranslation()
  const { control, trigger } = useFormContext<PromoNotificationFormValues>()
  const [promoPlans, isFetching] = useGetCurrentPromoPlans(id)
  const rebate = useWatch({ control, name: 'flashPlanRateRebate' })
  const dialog = useCustomBuydownDialog(id, rebate)

  const customBuydown = promoPlans.find((plan) => plan.family === CUSTOM_BUYDOWN_FAMILY) ?? null

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.notificationsTitle')}
          subheader={t('merchant.customBuydown.notificationsDescription')}
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledChipsField
            name="promoNotificationEmailList"
            control={control}
            trigger={trigger}
            label={t('merchant.customBuydown.notificationEmails')}
            placeholder={t('merchant.customBuydown.notificationEmailsPlaceholder')}
            helperText={t('merchant.customBuydown.notificationEmailsHelper')}
            disabled={readOnly}
            format={(v) => v.toLowerCase()}
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.rebateTitle')}
          subheader={
            <Stack direction="row" alignItems="center" gap={0.5}>
              <span>{t('merchant.customBuydown.rebateDescription')}</span>
              <Help title={t('merchant.customBuydown.rebateTooltip')} />
            </Stack>
          }
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledPercentField
            name="flashPlanRateRebate"
            control={control}
            label={t('merchant.customBuydown.rebate')}
            disabled={readOnly || rebateReadOnly}
            fullWidth
            sx={{ maxWidth: 300 }}
          />
        </CardContent>
      </Card>

      {customBuydown ? (
        <BuydownCard
          plan={customBuydown}
          rebate={rebate}
          onEdit={readOnly ? undefined : () => void dialog.edit(customBuydown)}
        />
      ) : (
        !readOnly && (
          <AreaButton
            onClick={() => void dialog.create()}
            icon={<AddIcon fontSize="large" />}
            title={t('merchant.customBuydown.emptyTitle')}
            description={t('merchant.customBuydown.emptyDescription')}
          />
        )
      )}
    </>
  )
}

export default CustomPromotionPanel
